import { useEffect, useRef } from "react";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Image as ImageIcon, Link as LinkIcon, Quote, Undo2, Redo2 } from "lucide-react";

/**
 * Simple contenteditable rich text editor.
 * Supports bold, italic, H2, H3, lists, blockquote, link, image (via URL).
 * Output: HTML string via onChange.
 */
export default function RichTextEditor({ value, onChange, placeholder = "Yazmaya başla…" }) {
  const ref = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    if (!initialized.current) {
      ref.current.innerHTML = value || "";
      initialized.current = true;
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    if (onChange) onChange(ref.current.innerHTML);
  };

  const wrapBlock = (tag) => {
    document.execCommand("formatBlock", false, tag);
    ref.current?.focus();
    if (onChange) onChange(ref.current.innerHTML);
  };

  const insertLink = () => {
    const url = window.prompt("Bağlantı URL'si:");
    if (url) exec("createLink", url);
  };

  const insertImage = () => {
    const url = window.prompt("Görsel URL'si:");
    if (url) exec("insertImage", url);
  };

  const handleInput = () => {
    if (onChange) onChange(ref.current.innerHTML);
  };

  const Btn = ({ onClick, title, children, testid }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="grid h-9 w-9 place-items-center rounded-lg text-ink-700 transition-colors hover:bg-cream-200"
    >
      {children}
    </button>
  );

  return (
    <div className="rounded-2xl border border-cream-300 bg-cream-50 overflow-hidden" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-1 border-b border-cream-300 bg-cream-100 px-2 py-2">
        <Btn onClick={() => exec("bold")} title="Kalın (Ctrl+B)" testid="editor-bold">
          <Bold className="h-4 w-4" />
        </Btn>
        <Btn onClick={() => exec("italic")} title="İtalik" testid="editor-italic">
          <Italic className="h-4 w-4" />
        </Btn>
        <div className="mx-1 h-6 w-px bg-cream-300" />
        <Btn onClick={() => wrapBlock("h2")} title="Alt başlık" testid="editor-h2">
          <Heading2 className="h-4 w-4" />
        </Btn>
        <Btn onClick={() => wrapBlock("h3")} title="Küçük başlık" testid="editor-h3">
          <Heading3 className="h-4 w-4" />
        </Btn>
        <Btn onClick={() => wrapBlock("p")} title="Paragraf" testid="editor-p">
          <span className="text-xs font-bold">P</span>
        </Btn>
        <Btn onClick={() => wrapBlock("blockquote")} title="Alıntı" testid="editor-quote">
          <Quote className="h-4 w-4" />
        </Btn>
        <div className="mx-1 h-6 w-px bg-cream-300" />
        <Btn onClick={() => exec("insertUnorderedList")} title="Liste" testid="editor-ul">
          <List className="h-4 w-4" />
        </Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Numaralı liste" testid="editor-ol">
          <ListOrdered className="h-4 w-4" />
        </Btn>
        <div className="mx-1 h-6 w-px bg-cream-300" />
        <Btn onClick={insertLink} title="Bağlantı" testid="editor-link">
          <LinkIcon className="h-4 w-4" />
        </Btn>
        <Btn onClick={insertImage} title="Görsel" testid="editor-image">
          <ImageIcon className="h-4 w-4" />
        </Btn>
        <div className="mx-1 h-6 w-px bg-cream-300" />
        <Btn onClick={() => exec("undo")} title="Geri al">
          <Undo2 className="h-4 w-4" />
        </Btn>
        <Btn onClick={() => exec("redo")} title="Yinele">
          <Redo2 className="h-4 w-4" />
        </Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder}
        data-testid="rich-editor-area"
        className="k-prose min-h-[280px] px-5 py-4 outline-none"
      />
    </div>
  );
}
